/**
 * bridge/button_controller.js — Hotwire Native "button" bridge component (web side).
 *
 * Stimulus identifier: `bridge--button` (glob-derived from the bridge/ subdir).
 * Wire a button-like element with data-controller="bridge--button" plus
 * data-bridge-* attributes; inside a Hotwire Native shell this renders a real
 * native nav-bar button (title and/or SF Symbol image) and forwards its tap
 * back as a click() on this element. Outside a native shell BridgeComponent
 * never connect()s, so this is a graceful no-op on web and Android (which is
 * web-rendered for this component) — the element renders as its normal HTML.
 *
 * Name invariant: `static component` ("button") MUST equal the iOS
 * ButtonComponent `class var name`.
 *
 * Attributes (read via bridgeElement.bridgeAttribute, i.e. data-bridge-*):
 *   side          "left" | "right"  (default "right")
 *   ios-image     SF Symbol name for the iOS button image (optional)
 *   android-image image name for Android (forwarded, unused on iOS) (optional)
 *   color         "#RRGGBB" tint for the native button (optional)
 * Title comes from the element's `title` attribute.
 *
 * Adapted from Joe Masilotti's canonical button_controller
 * (github.com/joemasilotti/bridge-components).
 */
import { BridgeComponent } from "@hotwired/hotwire-native-bridge";

export default class extends BridgeComponent {
  static component = "button";

  connect() {
    super.connect();
    this.#addButton();
  }

  disconnect() {
    super.disconnect();
    this.#removeButton();
  }

  #addButton() {
    const element = this.bridgeElement;
    const side = element.bridgeAttribute("side") || "right";
    const iosImage = element.bridgeAttribute("ios-image");
    const androidImage = element.bridgeAttribute("android-image");
    const color = element.bridgeAttribute("color");
    const data = { title: element.title, iosImage, androidImage, color };

    this.send(side, data, () => {
      this.element.click();
    });
  }

  #removeButton() {
    this.send("disconnect");
  }
}
